// views/NotificationSchedulerView.tsx
import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Space, Input, Empty } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import ReminderConfigForm from '../components/scheduler/ReminderConfigForm';
import SchedulerStatusCard from '../components/scheduler/SchedulerStatusCard';
import ReminderConfigList from '../components/scheduler/ReminderConfigList';
import { useReminderSchedulerStore } from '../stores/reminder-scheduler-store';
import {
    useGetAllConfigurations,
    useCreateConfiguration,
    useUpdateConfiguration,
    useDeleteConfiguration,
    useToggleConfigurationStatus,
    useTriggerConfiguration,
    useGetConfigurationStatus, getConfigurationStatus
} from '@/shared/api/client/reminder-configuration-controller';
import { useGetAllQueryServices } from '@/shared/api/client/notification-process-service-controller';
import { ReminderConfigDTO } from '@/shared/api/model';

const { Title, Text } = Typography;
const { Search } = Input;

const NotificationSchedulerView: React.FC = () => {
    const { dark } = useTheme();
    const [searchTerm, setSearchTerm] = useState('');
    const [triggeringId, setTriggeringId] = useState<string | null>(null);
    const [scheduledConfigs, setScheduledConfigs] = useState<Record<string, boolean>>({});

    // Get state and actions from store
    const {
        formData,
        isFormVisible,
        isEditMode,
        activeConfigId,
        setFormData,
        setIsFormVisible,
        setIsEditMode,
        setActiveConfigId,
        resetForm
    } = useReminderSchedulerStore();

    // API hooks (using orval-generated hooks)
    const { data: configsData, isLoading: isLoadingConfigs, refetch: refetchConfigs } = useGetAllConfigurations();
    const { data: queryServicesData, isLoading: isLoadingServices } = useGetAllQueryServices();
    const { mutateAsync: createConfig } = useCreateConfiguration();
    const { mutateAsync: updateConfig } = useUpdateConfiguration();
    const { mutateAsync: deleteConfig } = useDeleteConfiguration();
    const { mutateAsync: toggleStatus } = useToggleConfigurationStatus();
    const { mutateAsync: triggerConfig } = useTriggerConfiguration();

    // Derived data
    const configs = configsData || [];
    const queryServices = queryServicesData || [];

    // Load scheduler status for each config on initial load
    useEffect(() => {
        if (configs.length > 0) {
            loadSchedulerStatus();
        }
    }, [configs]);

    // Load scheduler status for configurations
    const loadSchedulerStatus = async () => {
        const statusMap: Record<string, boolean> = {};

        for (const config of configs) {
             if (!config.id) continue;
            try {
                const { data } = await getConfigurationStatus(config.id);
                // @ts-ignore
                statusMap[config.id] = data?.scheduled || false;
            } catch (error) {
                console.error('Error fetching config status:', error);
                statusMap[config.id] = false;
            }
        }

        setScheduledConfigs(statusMap);
    };

    // Filter configs based on search term
    const filteredConfigs = configs.filter(config => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            (config.name?.toLowerCase().includes(term) || false) ||
            (config.description?.toLowerCase().includes(term) || false) ||
            (config.templateName?.toLowerCase().includes(term) || false) ||
            (config.queryServiceName?.toLowerCase().includes(term) || false)
        );
    });

    // Create new config
    const createNewConfig = () => {
        resetForm();
        setIsEditMode(false);
        setActiveConfigId(null);
        setIsFormVisible(true);
    };

    // Edit existing config
    const editConfig = (id: string) => {
        const config = configs.find(c => c.id === id);
        if (config) {
            setFormData({ ...config });
            setIsEditMode(true);
            setActiveConfigId(id);
            setIsFormVisible(true);
        }
    };

    // Delete config
    const removeConfig = async (id: string) => {
        try {
            await deleteConfig({ id });
            refetchConfigs();
        } catch (error) {
            console.error('Error deleting config:', error);
        }
    };

    // Toggle enabled status
    const toggleEnabledStatus = async (id: string, enabled: boolean) => {
        try {
            await toggleStatus({ id, params: { enabled } });
            refetchConfigs();
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    // Update form field
    const updateFormField = (field: keyof ReminderConfigDTO, value: any) => {
        const currentFormData = useReminderSchedulerStore.getState().formData;
        const updatedFormData = {
            ...currentFormData,
            [field]: value
        };

        // Log the update for debugging
        console.log(`Updating ${field} to:`, value);

        // Update the form data state
        setFormData(updatedFormData);
    };

// Server-side data synchronization fix

// Update the saveConfig function to log everything and handle the data properly
    const saveConfig = async () => {
        try {
            console.log('About to save config with current formData:', formData);

            // Extract the data directly from the formData
            const configToSave = { ...formData };

            // Convert hour and minute to numbers (not strings)
            if (typeof configToSave.reminderHour !== 'number') {
                configToSave.reminderHour = parseInt(configToSave.reminderHour || '8', 10);
            }

            if (typeof configToSave.reminderMinute !== 'number') {
                configToSave.reminderMinute = parseInt(configToSave.reminderMinute || '0', 10);
            }

            console.log('Sanitized config data for API:', configToSave);

            if (isEditMode && activeConfigId) {
                console.log('Updating existing config with ID:', activeConfigId);
                await updateConfig({
                    id: activeConfigId,
                    data: configToSave
                });
            } else {
                console.log('Creating new config');
                await createConfig({
                    data: configToSave
                });
            }

            console.log('Save operation completed successfully');
            refetchConfigs();
            return true;
        } catch (error) {
            console.error('Error saving config:', error);
            return false;
        }
    };

    // Trigger config manually
    const handleTriggerConfig = async (id: string) => {
        setTriggeringId(id);
        try {
            await triggerConfig({ id });
            // Refresh configs after triggering
            refetchConfigs();
            // Update scheduler status
            loadSchedulerStatus();
        } catch (error) {
            console.error('Error triggering config:', error);
        } finally {
            setTriggeringId(null);
        }
    };

    // Handle refresh
    const handleRefresh = () => {
        refetchConfigs();
        loadSchedulerStatus();
    };

    return (
        <div className="space-y-6">
            <Card
                className={dark ? 'bg-gray-800 text-white' : ''}
                title={
                    <div className="flex justify-between items-center">
                        <Title level={4} className={dark ? 'text-white mb-0' : 'mb-0'}>
                            Notification Scheduler
                        </Title>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleRefresh}
                                loading={isLoadingConfigs}
                            >
                                Refresh
                            </Button>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={createNewConfig}
                            >
                                Create Schedule
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Text className="block mb-6">
                    Configure automated notification schedules for various types of notifications.
                </Text>

                <SchedulerStatusCard onRefresh={handleRefresh} />

                <div className="mb-4">
                    <Search
                        placeholder="Search by name or description"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: 300 }}
                        allowClear
                    />
                </div>

                {filteredConfigs.length > 0 ? (
                    <ReminderConfigList
                        configs={filteredConfigs}
                        onEdit={editConfig}
                        onDelete={removeConfig}
                        onToggleStatus={toggleEnabledStatus}
                        onTrigger={handleTriggerConfig}
                        isLoading={isLoadingConfigs}
                        triggeringId={triggeringId}
                        scheduledConfigs={scheduledConfigs}
                    />
                ) : (
                    <Empty
                        description={
                            <span className={dark ? 'text-gray-300' : ''}>
                                {isLoadingConfigs ? 'Loading configurations...' : 'No reminder configurations found'}
                            </span>
                        }
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                )}
            </Card>

            {isFormVisible && (
                <ReminderConfigForm
                    visible={isFormVisible}
                    onCancel={() => setIsFormVisible(false)}
                    formData={formData}
                    isEditMode={isEditMode}
                    isLoading={isLoadingConfigs}
                    // @ts-ignore
                    updateFormField={updateFormField}
                    saveConfig={saveConfig}
                    queryServices={queryServices}
                    isLoadingServices={isLoadingServices}
                />
            )}
        </div>
    );
};

export default NotificationSchedulerView;